import { forwardRef } from 'react';
import { useAppSelector } from '../../hooks';
import { motion } from 'framer-motion';

import {
  selectHasCustomHelpButtonIcon,
  selectHelpButtonIcon,
  selectIsPositionLeft,
  selectLaunchStyle,
} from 'reducers/config';
import { selectWorkflowsConfig } from 'reducers/workflows';

import { css, styled } from '@mui/material/styles';
import WidgetDescription from './WidgetDescription';
import { FloatingBar } from './FloatingBar';
import { Box } from '@mui/material';
import {
  FORETHOUGHT_CUSTOM_ICON_ID,
  FORETHOUGHT_HELP_BUTTON_ID,
} from 'embed/constants';

interface HelpButtonProps {
  /** function to be used onClick */
  onClickHandler: () => void;
}

export const HelpButton = motion.create(
  forwardRef<HTMLButtonElement, HelpButtonProps>(function HelpButton(
    { onClickHandler },
    ref,
  ) {
    const helpButtonIcon = useAppSelector(selectHelpButtonIcon);
    const { use_help_button_image_as_icon: useCustomIcon } =
      useAppSelector(selectWorkflowsConfig) || {};
    const hasCustomHelpButtonIcon = useAppSelector(
      selectHasCustomHelpButtonIcon,
    );
    const isPositionLeft = useAppSelector(selectIsPositionLeft);
    const launchStyle = useAppSelector(selectLaunchStyle);

    if (launchStyle === 'floating_bar') {
      return (
        <Box id={FORETHOUGHT_HELP_BUTTON_ID} ref={ref} sx={{ width: '100%' }}>
          <FloatingBar />
        </Box>
      );
    }

    if (useCustomIcon) {
      return (
        <CustomHelpButton
          aria-describedby='forethought-widget-description'
          aria-label='Open Forethought Solve widget'
          id={FORETHOUGHT_HELP_BUTTON_ID}
          onClick={onClickHandler}
          ref={ref}
        >
          <CustomImage
            alt=''
            id={FORETHOUGHT_CUSTOM_ICON_ID}
            src={helpButtonIcon}
          />
          <WidgetDescription />
        </CustomHelpButton>
      );
    }

    return (
      <HelpButtonContainer
        aria-describedby='forethought-widget-description'
        aria-label='Open Forethought Solve widget'
        hasCustomHelpButtonIcon={hasCustomHelpButtonIcon}
        id={FORETHOUGHT_HELP_BUTTON_ID}
        isPositionLeft={isPositionLeft}
        onClick={onClickHandler}
        ref={ref}
      >
        <HelpButtonContentContainer>
          <HelpButtonImage
            alt=''
            hasCustomHelpButtonIcon={hasCustomHelpButtonIcon}
            src={helpButtonIcon}
          />
        </HelpButtonContentContainer>
        <WidgetDescription />
      </HelpButtonContainer>
    );
  }),
);

const HelpButtonContainer = styled('button')<{
  hasCustomHelpButtonIcon: boolean;
  isPositionLeft: boolean;
}>`
  align-items: center;
  align-self: ${props => (props.isPositionLeft ? 'flex-start' : 'flex-end')};
  border: transparent;
  border-radius: 50%;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  height: 60px;
  justify-content: center;
  outline: none;
  padding: 0;
  width: 60px;
  background-color: transparent;
  transition: transform 0.1s ease-out;

  &:hover {
    transform: scale(1.1);
  }

  &:focus-visible {
    div {
      background-color: var(--hovered-theme);
      /* inner indicator */
      outline: 2px ${props => props.theme.palette.colors.white} solid;
      /* outer indicator */
      box-shadow: 0 0 0 4px ${props => props.theme.palette.colors.black};
    }
  }

  ${props =>
    props.hasCustomHelpButtonIcon &&
    css`
      &:focus-visible {
        img {
          /* inner indicator */
          outline: 2px ${props.theme.palette.colors.white} solid;
          /* outer indicator */
          box-shadow: 0 0 0 4px ${props.theme.palette.colors.black};
        }
      }
    `}
`;

const HelpButtonContentContainer = styled('div')`
  align-items: center;
  background-color: var(--theme);
  border-radius: 50%;
  color: ${props => props.theme.palette.colors.white};
  display: flex;
  height: 60px;
  justify-content: center;
  outline: none;
  width: 60px;
`;

const HelpButtonImage = styled('img')<{ hasCustomHelpButtonIcon: boolean }>`
  ${props =>
    props.hasCustomHelpButtonIcon &&
    `
      border-radius: 50%;
      height: 60px;
      object-fit: contain;
      width: 60px;
      box-shadow: 0 0 1px 0px ${props.theme.palette.colors.white} inset, 0 0 1px 0px ${props.theme.palette.colors.white};
    `}
`;

const CustomHelpButton = styled('button')`
  background-color: transparent;
  border: transparent;
  display: flex;
  justify-content: end;
  margin: 0;
  outline: none;
  overflow: hidden;
  padding: 0;
  cursor: pointer;
  transition: transform 0.1s ease-out;

  &:hover {
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    /* inner indicator */
    outline: 2px ${props => props.theme.palette.colors.white} solid;
    /* outer indicator */
    box-shadow: 0 0 0 4px ${props => props.theme.palette.colors.black};
  }
`;

const CustomImage = styled('img')`
  max-height: 60px;
`;
